import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, ShoppingCart, RotateCcw, Coffee, UtensilsCrossed, Cake, GlassWater } from 'lucide-react';
import DOMPurify from 'dompurify';
import { api } from '@/utils/apiClient';

// Custom event to notify other components when chat opens/closes
const CHAT_STATE_EVENT = 'bulaccino-chat-state';

// Pages where the chat widget should be hidden (internal/operational pages only)
// Chat widget is ONLY for public-facing website pages
const HIDDEN_ON_PAGES = [
  '/pay/',           // Payment/checkout pages
  '/checkout',       // Checkout flow
  '/order',          // Order/cart page - don't distract from checkout!
  '/bula-coffee-club', // Coffee Pass page - focus on reading & ordering from page
  '/kiosk',          // Self-service kiosk
  '/pos',            // Point of sale
  '/server',         // Server order entry
  '/pickup',         // Pickup display
  '/display',        // Window displays (display1, display2, display3)
  '/staff-display',  // Staff order display
  '/order-display',  // Order status display
  '/admin',          // Admin panel
  '/kiosk-queue',    // Kiosk queue display
  '/baked-display',  // Baked goods display alternate route
  '/share',          // Photo sharing page - focus on sharing only, no ordering CTA
  '/staff',          // Staff portal pages (clock, login, profile)
  '/customer-display', // Customer facing display at POS
  '/kds',            // Kitchen display system pages
  '/training',       // Training mode pages
  '/catering',       // Catering portal - has its own ordering flow
];

// Quick action buttons for easy navigation
const QUICK_ACTIONS = [
  { id: 'menu', label: 'Menu', icon: UtensilsCrossed, message: 'menu' },
  { id: 'coffee', label: 'Coffee', icon: Coffee, message: 'show me coffee' },
  { id: 'breakfast', label: 'Breakfast', icon: UtensilsCrossed, message: 'show me breakfast' },
  { id: 'sweets', label: 'Sweets', icon: Cake, message: 'show me sweets' },
  { id: 'drinks', label: 'Cold Drinks', icon: GlassWater, message: 'show me iced drinks' },
];

const ChatWidget = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [sessionState, setSessionState] = useState('greeting');
  const [showQuickActions, setShowQuickActions] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Check if widget should be hidden on current page (memoized to avoid recalculating)
  const shouldHideWidget = useMemo(() => {
    const path = location.pathname;
    // Check exact matches and prefix matches
    return HIDDEN_ON_PAGES.some(page => 
      path === page || path.startsWith(page + '/') || path.includes(page)
    );
  }, [location.pathname]);

  // Dispatch event when chat opens/closes to hide/show other elements
  useEffect(() => {
    if (shouldHideWidget) return;
    
    window.dispatchEvent(new CustomEvent(CHAT_STATE_EVENT, { detail: { isOpen } }));
    
    if (isOpen) {
      document.body.classList.add('chat-widget-open');
    } else {
      document.body.classList.remove('chat-widget-open');
    }
    
    return () => {
      document.body.classList.remove('chat-widget-open');
    };
  }, [isOpen, shouldHideWidget]);

  // Generate a unique session ID for this chat
  useEffect(() => {
    const storedSession = localStorage.getItem('bulaccino_chat_session');
    if (storedSession) {
      setSessionId(storedSession);
    } else {
      const newSession = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('bulaccino_chat_session', newSession);
      setSessionId(newSession);
    }
  }, []);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Send initial greeting when chat first opens
  useEffect(() => {
    if (isOpen && messages.length === 0 && sessionId) {
      sendMessage('bula', true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, sessionId]);

  const sendMessage = async (text, isInitial = false) => {
    if (!text.trim() || isLoading) return;

    // Add user message to chat (unless it's the initial greeting)
    if (!isInitial) {
      setMessages(prev => [...prev, { type: 'user', text: text.trim() }]);
      setShowQuickActions(false);
    }
    
    setInputValue('');
    setIsLoading(true);

    try {
      const response = await api.post('/chat/message', {
        session_id: sessionId,
        message: text.trim()
      });

      const data = response.data;
      
      if (data.response) {
        setMessages(prev => [...prev, { type: 'bot', text: data.response }]);
        
        // Update cart count and session state
        if (data.cart_items !== undefined) {
          setCartCount(data.cart_items);
        }
        if (data.session_state) {
          setSessionState(data.session_state);
        }

        // Check if response contains a payment link
        if (data.response.includes('/pay/')) {
          const payMatch = data.response.match(/https?:\/\/[^\s]+\/pay\/[A-Z0-9-]+/i);
          if (payMatch) {
            // Store the payment URL for the checkout button
            setMessages(prev => {
              const updated = [...prev];
              updated[updated.length - 1].paymentUrl = payMatch[0];
              return updated;
            });
          }
        }
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        type: 'bot', 
        text: 'Sorry, I had trouble connecting. Please try again or visit our menu to order online.',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleQuickAction = (action) => {
    sendMessage(action.message);
  };

  const startNewChat = () => {
    const newSession = `web_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem('bulaccino_chat_session', newSession);
    setSessionId(newSession);
    setMessages([]);
    setCartCount(0);
    setSessionState('greeting');
    setShowQuickActions(true);
    sendMessage('bula', true);
  };

  const handlePaymentClick = (url) => {
    // Extract order number from URL and navigate
    const orderMatch = url.match(/\/pay\/([A-Z0-9-]+)/i);
    if (orderMatch) {
      setIsOpen(false);
      navigate(`/pay/${orderMatch[1]}`);
    } else {
      window.open(url, '_blank');
    }
  };

  // Format message text with WhatsApp-style formatting
  const formatMessage = (text, paymentUrl = null) => {
    const lines = text.split('\n');
    const elements = [];
    
    lines.forEach((line, i) => {
      // Bold text between asterisks
      let formatted = line.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
      
      // Convert URLs to clickable links with proper styling
      formatted = formatted.replace(
        /(https?:\/\/[^\s]+)/g, 
        '<a href="$1" target="_blank" rel="noopener noreferrer" class="chat-link">$1</a>'
      );
      
      // Sanitize the formatted HTML to prevent XSS
      const sanitizedHtml = DOMPurify.sanitize(formatted, {
        ALLOWED_TAGS: ['a', 'strong', 'em', 'b', 'i', 'br'],
        ALLOWED_ATTR: ['href', 'target', 'rel', 'class']
      });
      
      // Handle emoji bullets
      if (line.match(/^[•●◦‣⁃▪▸►]/)) {
        elements.push(
          <p key={i} className="mb-0.5 pl-1" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        );
      } else if (line.trim() === '') {
        elements.push(<div key={i} className="h-2" />);
      } else {
        elements.push(
          <p key={i} className="mb-1" dangerouslySetInnerHTML={{ __html: sanitizedHtml }} />
        );
      }
    });

    return (
      <div className="whitespace-pre-wrap">
        {elements}
        {paymentUrl && (
          <button
            onClick={() => handlePaymentClick(paymentUrl)}
            className="mt-3 w-full py-2.5 px-4 rounded-xl font-semibold text-sm transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            style={{ backgroundColor: '#25D366', color: '#ffffff' }}
            data-testid="chat-checkout-btn"
          >
            <ShoppingCart className="w-4 h-4 inline mr-2" />
            Complete Checkout
          </button>
        )}
      </div>
    );
  };

  // Don't render anything if we should hide the widget (on payment/checkout pages)
  if (shouldHideWidget) {
    return null;
  }

  return (
    <>
      {/* Custom styles for chat links */}
      <style>{`
        .chat-link {
          color: #2563eb !important;
          text-decoration: underline !important;
        }
        .chat-link:hover {
          color: #1d4ed8 !important;
        }
      `}</style>

      {/* Chat Button - Fixed WhatsApp green */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 px-5 py-3 rounded-full shadow-lg transition-all duration-300 hover:scale-105 active:scale-95 ${
          isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'
        }`}
        style={{ backgroundColor: '#25D366' }}
        aria-label="Open chat"
        data-testid="chat-widget-button"
      >
        <MessageCircle className="w-6 h-6 text-white" />
        <span className="text-white font-semibold text-sm">Tap to Order</span>
        {cartCount > 0 && (
          <span 
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold"
            style={{ backgroundColor: '#ef4444', color: '#ffffff' }}
          >
            {cartCount}
          </span>
        )}
      </button>

      {/* Chat Window - Forced light mode for consistency */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[380px] max-w-[calc(100vw-48px)] h-[600px] max-h-[calc(100vh-100px)] rounded-2xl shadow-2xl flex flex-col overflow-hidden transition-all duration-300 ${
          isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'
        }`}
        style={{ backgroundColor: '#ffffff' }}
        data-testid="chat-widget-window"
      >
        {/* Header */}
        <div 
          className="flex items-center justify-between px-4 py-3"
          style={{ backgroundColor: '#25D366', color: '#ffffff' }}
        >
          <div className="flex items-center gap-3">
            <div 
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}
            >
              <span className="text-xl">☕</span>
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-sm leading-tight" style={{ color: '#ffffff' }}>Bulaccino Cafe</h3>
              <p className="text-xs leading-tight flex items-center gap-1" style={{ color: 'rgba(255,255,255,0.9)' }}>
                <span className="w-2 h-2 rounded-full bg-green-300 animate-pulse"></span>
                Online • Order Assistant
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {cartCount > 0 && (
              <div 
                className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium"
                style={{ backgroundColor: 'rgba(255,255,255,0.2)', color: '#ffffff' }}
              >
                <ShoppingCart className="w-3 h-3" />
                {cartCount}
              </div>
            )}
            <button
              onClick={startNewChat}
              className="p-1.5 rounded-full transition-colors hover:bg-white/20"
              style={{ color: '#ffffff' }}
              title="Start new chat"
              aria-label="Start new chat"
            >
              <RotateCcw className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1.5 rounded-full transition-colors hover:bg-white/20"
              style={{ color: '#ffffff' }}
              aria-label="Close chat"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div 
          className="flex-1 overflow-y-auto p-4 space-y-3"
          style={{ backgroundColor: '#f0f2f5' }}
        >
          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] px-4 py-2.5 rounded-2xl text-sm ${
                  msg.isError ? 'border-2 border-red-200' : ''
                }`}
                style={msg.type === 'user' 
                  ? { 
                      backgroundColor: '#25D366', 
                      color: '#ffffff', 
                      borderBottomRightRadius: '4px' 
                    }
                  : { 
                      backgroundColor: '#ffffff', 
                      color: '#1f2937', 
                      borderBottomLeftRadius: '4px', 
                      boxShadow: '0 1px 2px rgba(0,0,0,0.08)' 
                    }
                }
              >
                {msg.type === 'user' ? (
                  <p>{msg.text}</p>
                ) : (
                  formatMessage(msg.text, msg.paymentUrl)
                )}
              </div>
            </div>
          ))}
          
          {isLoading && (
            <div className="flex justify-start">
              <div 
                className="px-4 py-3 rounded-2xl"
                style={{ backgroundColor: '#ffffff', borderBottomLeftRadius: '4px', boxShadow: '0 1px 2px rgba(0,0,0,0.08)' }}
              >
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }}></span>
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }}></span>
                  <span className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }}></span>
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions - Show when appropriate */}
        {showQuickActions && messages.length > 0 && !isLoading && (
          <div 
            className="px-3 py-2 flex gap-2 overflow-x-auto scrollbar-hide"
            style={{ backgroundColor: '#ffffff', borderTop: '1px solid #e5e7eb' }}
          >
            {QUICK_ACTIONS.map((action) => (
              <button
                key={action.id}
                onClick={() => handleQuickAction(action)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-all hover:scale-105 active:scale-95"
                style={{ 
                  backgroundColor: '#f3f4f6', 
                  color: '#374151',
                  border: '1px solid #e5e7eb'
                }}
                data-testid={`quick-action-${action.id}`}
              >
                <action.icon className="w-3.5 h-3.5" />
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Input Area */}
        <form 
          onSubmit={handleSubmit} 
          className="p-3"
          style={{ backgroundColor: '#ffffff', borderTop: '1px solid #e5e7eb' }}
        >
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type your message..."
              className="flex-1 px-4 py-2.5 rounded-full text-sm transition-all focus:ring-2 focus:ring-green-500/30"
              style={{ 
                backgroundColor: '#f3f4f6',
                color: '#1f2937',
                border: '1px solid #e5e7eb',
                outline: 'none'
              }}
              disabled={isLoading}
              data-testid="chat-input"
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isLoading}
              className="p-2.5 rounded-full transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:scale-105 active:scale-95"
              style={{ backgroundColor: '#25D366', color: '#ffffff' }}
              aria-label="Send message"
              data-testid="chat-send-button"
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
          <p className="text-[10px] text-center mt-2" style={{ color: '#9ca3af' }}>
            Pickup orders only • Powered by Bulaccino AI
          </p>
        </form>
      </div>
    </>
  );
};

export default ChatWidget;
